import type { KeyboardEvent } from "react";
import { Link } from "react-router";
import { cn } from "~/lib/cn";
import { Button } from "./Button";

export type TabOption<T extends string> = {
  id: T;
  label: string;
  /** T872 S3 — the mono line above the label on the `phase` variant («01»). */
  kicker?: string;
};

export type TabsVariant = "line" | "account" | "section" | "phase";

type BaseProps<T extends string> = {
  value: T;
  options: TabOption<T>[];
  ariaLabel: string;
  className?: string;
  /** `line`: bordered bar (dashboard, logbook). `account`: top account nav (Perfil / Pagos). `section`: profile sub-tabs under the page title. `phase`: the public phase explorer (T872 S3, `.pub-phase-tab`), number + name with the pink underline — styled in app.css, not here. */
  variant?: TabsVariant;
  /**
   * T872 S3 — when the tabs own panels elsewhere in the DOM, the prefix that names them:
   * tab `<prefix>-tab-<id>`, panel `<prefix>-panel-<id>` (`aria-controls`). The panel
   * carries `aria-labelledby="<prefix>-tab-<id>"`.
   */
  idPrefix?: string;
};

export type TabsProps<T extends string> = BaseProps<T> &
  (
    | { getHref: (id: T) => string; onChange?: never }
    | { onChange: (id: T) => void; getHref?: never }
  );

const navVariants: Record<TabsVariant, string> = {
  line: "mb-6 flex gap-6 overflow-x-auto border-b border-borderLight hide-scrollbar text-left md:gap-8",
  account:
    "flex flex-row flex-wrap gap-x-8 gap-y-2 overflow-x-auto hide-scrollbar text-left md:gap-x-10",
  section:
    "mb-8 flex flex-row gap-6 overflow-x-auto border-b border-borderLight pb-4 [-webkit-overflow-scrolling:touch] hide-scrollbar text-left md:gap-8",
  phase: "pub-phase-tabs",
};

const tabVariants: Record<TabsVariant, string> = {
  line: "shrink-0 border-b-2 pb-3 text-[10px] font-bold uppercase tracking-[0.2em] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-textMain focus-visible:ring-offset-2 md:text-xs",
  account:
    "whitespace-nowrap border-b-2 pb-3 text-left text-[10px] font-bold uppercase tracking-[0.2em] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-textMain focus-visible:ring-offset-2 md:text-xs",
  section:
    "whitespace-nowrap border-b-2 pb-3 text-left text-[10px] font-bold uppercase tracking-[0.2em] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-textMain focus-visible:ring-offset-2 md:text-xs",
  phase: "pub-phase-tab",
};

function tabActiveClasses(active: boolean, isLink: boolean) {
  if (active) return "border-core text-textMain";
  return cn(
    "border-transparent text-textSecondary",
    isLink ? "hover:border-borderLight hover:text-textMain" : "hover:text-textMain",
  );
}

export function Tabs<T extends string>(props: TabsProps<T>) {
  const { value, options, ariaLabel, className, variant = "line", idPrefix } = props;
  const getHref = "getHref" in props ? props.getHref : undefined;
  const onChange = "onChange" in props ? props.onChange : undefined;
  const isLink = getHref !== undefined;
  const navClass = navVariants[variant];
  const tabClass = tabVariants[variant];

  const children = options.map((opt) => {
    const active = value === opt.id;
    // The phase variant reads its selected state from `aria-selected` in app.css; the
    // Tailwind active classes of the other variants would paint a second underline.
    const merged = variant === "phase" ? tabClass : cn(tabClass, tabActiveClasses(active, isLink));
    const label = opt.kicker ? (
      <>
        <span className="pub-phase-tab-number">{opt.kicker}</span>
        <span className="pub-phase-tab-name">{opt.label}</span>
      </>
    ) : (
      opt.label
    );
    if (getHref) {
      return (
        <Link
          key={opt.id}
          to={getHref(opt.id)}
          replace
          preventScrollReset
          aria-current={active ? "page" : undefined}
          className={merged}
        >
          {label}
        </Link>
      );
    }
    return (
      <Button
        variant="unstyled"
        key={opt.id}
        id={idPrefix ? `${idPrefix}-tab-${opt.id}` : undefined}
        type="button"
        role="tab"
        aria-selected={active}
        aria-controls={idPrefix ? `${idPrefix}-panel-${opt.id}` : undefined}
        // T872 S2 — roving tabindex: Tab lands on the selected tab only; arrows move focus.
        tabIndex={active ? 0 : -1}
        onClick={() => onChange?.(opt.id)}
        className={merged}
      >
        {label}
      </Button>
    );
  });

  // Why two branches: mixing the <nav> landmark with role="tablist" conflates
  // page navigation with content-switching. WAI-ARIA Authoring Practices §tab
  // pattern keeps them separate — links inside <nav> announce as navigation;
  // role="tab" buttons inside role="tablist" announce as a tab widget. The
  // tab pattern also requires arrow-key focus management (below); navigation
  // gets aria-current="page" instead.
  if (isLink) {
    return (
      <nav className={cn(navClass, className)} aria-label={ariaLabel}>
        {children}
      </nav>
    );
  }

  // Arrow-key navigation for the tablist: WAI-ARIA tab pattern requires the
  // owner to handle ←/→/Home/End focus moves. Focus lives on individual
  // role="tab" buttons; onKeyDown bubbles up from there.
  const onTablistKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'));
    if (tabs.length === 0) return;
    const current = document.activeElement instanceof HTMLElement ? tabs.indexOf(document.activeElement) : -1;
    let next = current;
    if (e.key === "ArrowRight") next = (current + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (current - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next !== current && tabs[next]) {
      e.preventDefault();
      tabs[next].focus();
    }
  };

  return (
    // role="tablist" is a composite widget per WAI-ARIA; the container itself
    // does NOT take focus — focus lives on the role="tab" children and arrow
    // keys move it via onTablistKeyDown (event bubbles). jsx-a11y/interactive-
    // supports-focus doesn't distinguish composite roles from standalone
    // interactive ones, so it flags this as a false positive.
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      className={cn(navClass, className)}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onTablistKeyDown}
    >
      {children}
    </div>
  );
}
